import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { PageHeader } from '../components/ui/PageHeader';
import { Spinner } from '../components/ui/Spinner';
import { Markdown } from '../components/ui/Markdown';
import { useToast } from '../components/ui/Toast';
import { UsageBadge } from '../components/UsageBadge';
import { ArgusReviewPanel } from '../components/ArgusReviewPanel';
import { PreviewEnvironmentPanel } from '../components/PreviewEnvironmentPanel';
import { ReleaseRehearsalPanel } from '../components/ReleaseRehearsalPanel';
import { useApi } from '../hooks/useApi';
import { useRefetchOnFocus } from '../hooks/useRefetchOnFocus';
import { StatusBadge } from '../components/ui/StatusBadge';
import { EmptyState } from '../components/ui/EmptyState';
import type { AthenaInitiative, AthenaWorkstream } from '@pantheon/shared';

export function AthenaPage() {
  const { get, post, del } = useApi();
  const { toast } = useToast();
  const { initiativeId: urlInitiativeId } = useParams();
  const navigate = useNavigate();
  const [initiatives, setInitiatives] = useState<AthenaInitiative[]>([]);
  const [active, setActive] = useState<AthenaInitiative | null>(null);
  const [brief, setBrief] = useState('');
  const [feedback, setFeedback] = useState('');
  const [busy, setBusy] = useState(false);
  const getRef = useRef(get);
  getRef.current = get;

  const fetchList = useCallback(async () => {
    const result = await getRef.current<AthenaInitiative[]>('/athena/initiatives');
    if (result.success && result.data) setInitiatives(result.data);
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const activeRef = useRef(active);
  activeRef.current = active;

  // Load the initiative named in the URL so /athena/:id is a shareable deep link.
  const loadedIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!urlInitiativeId) { loadedIdRef.current = null; return; }
    if (urlInitiativeId === loadedIdRef.current) return;
    loadedIdRef.current = urlInitiativeId;
    void getRef.current<AthenaInitiative>(`/athena/initiatives/${urlInitiativeId}`).then((r) => {
      if (r.success && r.data) setActive(r.data);
    });
  }, [urlInitiativeId]);

  const openInitiative = useCallback((init: AthenaInitiative) => {
    setActive(init);
    loadedIdRef.current = init.initiativeId;
    navigate(`/athena/${init.initiativeId}`);
  }, [navigate]);

  const closeInitiative = useCallback(() => {
    setActive(null);
    loadedIdRef.current = null;
    navigate('/athena');
    fetchList();
  }, [navigate, fetchList]);

  // Poll the open initiative — fast while analyzing, slow otherwise so workstream and
  // review statuses keep updating instead of freezing once analysis finishes.
  useEffect(() => {
    if (!active) return;
    const analyzing = active.status === 'analyzing';
    const interval = setInterval(async () => {
      const result = await getRef.current<AthenaInitiative>(`/athena/initiatives/${active.initiativeId}`);
      if (result.success && result.data) {
        setActive(result.data);
        if (analyzing && result.data.status !== 'analyzing') fetchList();
      }
    }, analyzing ? 5000 : 25_000);
    return () => clearInterval(interval);
  }, [active?.initiativeId, active?.status, fetchList]);

  // Refresh the list + the open initiative when the user returns to the tab.
  const refetchAthena = useCallback(() => {
    fetchList();
    const a = activeRef.current;
    if (a) void getRef.current<AthenaInitiative>(`/athena/initiatives/${a.initiativeId}`).then((r) => {
      if (r.success && r.data) setActive(r.data);
    });
  }, [fetchList]);
  useRefetchOnFocus(refetchAthena);
  const [expandedWs, setExpandedWs] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!brief.trim()) return;
    setBusy(true);
    const result = await post<AthenaInitiative>('/athena/initiatives', { brief });
    if (result.success && result.data) {
      openInitiative(result.data);
      setBrief('');
      fetchList();
      toast('Athena is analyzing the initiative...', 'info');
    } else {
      toast(result.error?.message ?? 'Failed to create initiative', 'error');
    }
    setBusy(false);
  };

  const handleApprove = async () => {
    if (!active) return;
    setBusy(true);
    const result = await post<{ workstreams: AthenaWorkstream[] }>(`/athena/initiatives/${active.initiativeId}/approve`, {});
    if (result.success) {
      const refreshed = await getRef.current<AthenaInitiative>(`/athena/initiatives/${active.initiativeId}`);
      if (refreshed.success && refreshed.data) setActive(refreshed.data);
      fetchList();
      toast('Workstreams fanned out — review each plan on the Hermes page', 'success');
    } else {
      toast(result.error?.message ?? 'Approve failed', 'error');
    }
    setBusy(false);
  };

  const handleReanalyze = async () => {
    if (!active) return;
    setBusy(true);
    await post(`/athena/initiatives/${active.initiativeId}/reanalyze`, { feedback: feedback.trim() || undefined });
    setFeedback('');
    const refreshed = await getRef.current<AthenaInitiative>(`/athena/initiatives/${active.initiativeId}`);
    if (refreshed.success && refreshed.data) setActive(refreshed.data);
    toast('Re-analyzing with your feedback...', 'info');
    setBusy(false);
  };

  const input = 'w-full px-3 py-2 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary';

  return (
    <div className="max-w-5xl animate-rise">
      <PageHeader
        mark="A"
        markBg="bg-athena/12"
        markText="text-athena"
        title="Athena"
        subtitle={
          <>
            The overseer. Describe a big feature once — Athena finds every affected project and splits the work
            into parallel workstreams for the runner fleet.
          </>
        }
      />

      {!active && (
        <>
          {/* New initiative */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_1px_2px_rgba(15,23,42,.04),0_10px_30px_-14px_rgba(15,23,42,.18)] p-5 mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">New Initiative</label>
            <textarea
              className={`${input} resize-y`}
              rows={4}
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              placeholder="Describe the feature across your products, e.g. 'Add live play-by-play to PrestoStats 2.0 — data comes from the Gameday API...'"
            />
            <div className="flex justify-end mt-3">
              <Button onClick={handleCreate} loading={busy} disabled={!brief.trim()}>
                Analyze with Athena
              </Button>
            </div>
          </div>

          {/* Initiative list */}
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Initiatives</h3>
          {initiatives.length === 0 ? (
            <EmptyState title="No initiatives yet" hint="Describe a cross-project feature above — Athena splits it into workstreams." />
          ) : (
            <div className="space-y-2">
              {initiatives.map((init) => {
                return (
                  <div
                    key={init.initiativeId}
                    onClick={() => openInitiative(init)}
                    className="bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50 p-4 cursor-pointer hover:shadow-md hover:shadow-slate-200/60 transition-shadow flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <StatusBadge status={init.status} className="shrink-0" />
                      <span className="text-sm text-gray-700 truncate">{init.brief.substring(0, 120)}</span>
                    </div>
                    <span className="text-xs text-gray-500 shrink-0 ml-3">
                      {init.workstreams?.length ? `${init.workstreams.length} workstreams` : ''}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {active && (
        <div className="space-y-5">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <StatusBadge status={active.status} />
              <UsageBadge usage={active.usage} />
            </div>
            <div className="flex items-center gap-2">
              <Button variant="danger" size="sm" onClick={async () => {
                if (!confirm('Delete this initiative? (Fanned-out Hermes sessions are kept.)')) return;
                await del(`/athena/initiatives/${active.initiativeId}`);
                closeInitiative();
                toast('Initiative deleted', 'info');
              }}>Delete</Button>
              <Button variant="ghost" size="sm" onClick={closeInitiative}>Back</Button>
            </div>
          </div>

          {/* Brief */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50 p-5">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Brief</h3>
            <Markdown>{active.brief}</Markdown>
          </div>

          {/* Analyzing spinner */}
          {active.status === 'analyzing' && (
            <div className="flex flex-col items-center py-8 bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50">
              <Spinner size="lg" className="text-primary mb-4" />
              <p className="text-base font-medium text-gray-700">Athena is reading the project registry and planning workstreams...</p>
            </div>
          )}

          {/* Analysis */}
          {active.analysis && active.status !== 'analyzing' && (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50 p-5">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Impact Analysis</h3>
              <Markdown>{active.analysis}</Markdown>
            </div>
          )}

          {/* Workstreams */}
          {!!active.workstreams?.length && active.status !== 'analyzing' && (
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Workstreams ({active.workstreams.length})
              </h3>
              <div className="space-y-2">
                {active.workstreams.map((ws) => {
                  const open = expandedWs === ws.id;
                  return (
                    <div key={ws.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50 overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setExpandedWs(open ? null : ws.id)}
                        className="w-full text-left p-4 hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-center gap-2 flex-wrap min-w-0">
                            <svg className={`w-3.5 h-3.5 text-gray-500 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} viewBox="0 0 20 20" fill="currentColor"><path d="M7 5l6 5-6 5V5z" /></svg>
                            <span className="text-xs font-mono text-gray-500">{ws.id}</span>
                            <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-purple-100 text-purple-700">{ws.projectName}</span>
                            {ws.id.startsWith('review-') && (
                              <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-red-100 text-red-700">Argus fix</span>
                            )}
                            <span className="text-sm font-medium text-gray-800">{ws.title}</span>
                          </div>
                          <div className="text-xs text-gray-500 shrink-0 text-right">
                            {ws.dependsOn.length > 0 && <div>after {ws.dependsOn.join(', ')}</div>}
                            {ws.hermesSessionId && <div className="text-success">→ Hermes session</div>}
                          </div>
                        </div>
                        {!open && <p className="text-xs text-gray-500 whitespace-pre-wrap line-clamp-2 mt-1.5 pl-5">{ws.scopedBrief}</p>}
                      </button>
                      {open && (
                        <div className="px-4 pb-4 pl-9 border-t border-gray-100 pt-3">
                          <Markdown>{ws.scopedBrief}</Markdown>
                          {ws.hermesSessionId && (
                            <a href={`/hermes/${ws.hermesSessionId}`} className="inline-block mt-2 text-xs text-primary underline hover:no-underline">
                              Open its plan on the Hermes page →
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Actions */}
          {active.status === 'planned' && (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">
                  Approving creates one Hermes planning session per workstream — you still review each ticket plan.
                </span>
                <Button onClick={handleApprove} loading={busy}>Approve &amp; Fan Out</Button>
              </div>
              <div className="flex gap-2">
                <input
                  className={input}
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder="Feedback for a different split (optional)..."
                />
                <Button variant="secondary" onClick={handleReanalyze} loading={busy}>Re-analyze</Button>
              </div>
            </div>
          )}

          {active.status === 'approved' && (
            <div className="bg-success-light border border-green-200 rounded-xl p-4 text-sm text-green-800">
              Workstreams fanned out. Review and approve each plan on the <strong>Hermes</strong> page, then queue
              them from the <strong>Daedalus</strong> page — dependent workstreams wait automatically until their
              dependencies are merged.
            </div>
          )}

          {active.status === 'approved' && !!active.integrationBranch && active.previewEnvironment && (
            <PreviewEnvironmentPanel
              initiativeId={active.initiativeId}
              preview={active.previewEnvironment}
              onChange={setActive}
            />
          )}

          {active.status === 'approved' && (
            <ArgusReviewPanel
              initiativeId={active.initiativeId}
              canReview={!!active.integrationBranch}
              loopOutcome={active.reviewLoop?.outcome}
            />
          )}

          {active.status === 'approved' && (
            <ReleaseRehearsalPanel initiativeId={active.initiativeId} />
          )}

          {active.status === 'failed' && (
            <div className="bg-danger-light border border-red-200 rounded-xl p-4 text-sm text-red-800 flex items-center justify-between">
              <span>Analysis failed — check the brief and try again.</span>
              <Button variant="secondary" size="sm" onClick={handleReanalyze} loading={busy}>Retry</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
